"use client"

import { Box, HStack, Text, VStack } from "@chakra-ui/react"
import { AnimatePresence, domAnimation, LazyMotion, m } from "framer-motion"
import React, { useMemo, useState } from "react"

import { type DonutChartBlock, type DonutChartData } from "@/types/database"

interface DonutChartProps {
  block: DonutChartBlock
}

interface DonutChartSegment extends DonutChartData {
  percentage: number
  startAngle: number
  endAngle: number
  path: string
  tooltipX?: number
  tooltipY?: number
}

const FALLBACK_COLORS = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#8B5CF6",
  "#EC4899",
  "#64748B",
]

/**
 * Calculate SVG path for a donut segment
 */
function describeArc(
  x: number,
  y: number,
  radius: number,
  startAngle: number,
  endAngle: number
): string {
  const start = polarToCartesian(x, y, radius, endAngle)
  const end = polarToCartesian(x, y, radius, startAngle)
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1"

  return [
    "M",
    start.x,
    start.y,
    "A",
    radius,
    radius,
    0,
    largeArcFlag,
    0,
    end.x,
    end.y,
  ].join(" ")
}

function polarToCartesian(
  centerX: number,
  centerY: number,
  radius: number,
  angleInDegrees: number
): { x: number; y: number } {
  const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0
  return {
    x: centerX + radius * Math.cos(angleInRadians),
    y: centerY + radius * Math.sin(angleInRadians),
  }
}

const DonutChart: React.FC<DonutChartProps> = ({ block }) => {
  const {
    animationDuration = 1,
    centerContent,
    className = "",
    data = [],
    showLegend = true,
    showTooltip = true,
    size = 300,
    subtitle,
    thickness = 40,
    title,
  } = block

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 })

  // Gap between segments in degrees (approximately 6px visual gap)
  const segmentGap = 4
  const chartData = useMemo(
    () =>
      (Array.isArray(data) ? data : [])
        .map((item, index) => ({
          ...item,
          color: item.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length],
          label: item.label?.trim() || `Segment ${index + 1}`,
          value:
            typeof item.value === "number"
              ? item.value
              : Number.parseFloat(String(item.value)),
        }))
        .filter((item) => Number.isFinite(item.value) && item.value > 0),
    [data]
  )
  const total = useMemo(
    () => chartData.reduce((sum, item) => sum + item.value, 0),
    [chartData]
  )

  // Calculate segments
  const segments = useMemo(() => {
    const totalGaps = segmentGap * chartData.length
    let currentAngle = 0

    return chartData.map((item) => {
      const percentage = (item.value / total) * 100
      const angle = (percentage / 100) * (360 - totalGaps)
      const startAngle = currentAngle + segmentGap / 2
      const endAngle = startAngle + angle

      const centerX = size / 2
      const centerY = size / 2
      const radius = (size - thickness) / 2

      const path = describeArc(centerX, centerY, radius, startAngle, endAngle)

      // Calculate the midpoint angle for tooltip positioning
      const midAngle = (startAngle + endAngle) / 2
      const tooltipRadius = radius + thickness / 2
      const tooltipPoint = polarToCartesian(
        centerX,
        centerY,
        tooltipRadius,
        midAngle
      )

      currentAngle = endAngle + segmentGap / 2

      return {
        ...item,
        endAngle,
        path,
        percentage,
        startAngle,
        tooltipX: tooltipPoint.x,
        tooltipY: tooltipPoint.y,
      } as DonutChartSegment
    })
  }, [chartData, size, thickness, segmentGap, total])

  if (total <= 0) {
    return null
  }

  const handleMouseMove = (
    event: React.MouseEvent<SVGPathElement>,
    index: number
  ) => {
    setHoveredIndex(index)
    const rect = event.currentTarget.getBoundingClientRect()
    setTooltipPosition({
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    })
  }

  const handleLegendHover = (index: number) => {
    setHoveredIndex(index)
    // Use the pre-calculated tooltip position for the segment
    const segment = segments[index]
    if (
      segment &&
      segment.tooltipX !== undefined &&
      segment.tooltipY !== undefined
    ) {
      setTooltipPosition({
        x: segment.tooltipX,
        y: segment.tooltipY,
      })
    }
  }

  const handleMouseLeave = () => {
    setHoveredIndex(null)
  }

  return (
    <LazyMotion features={domAnimation}>
      <Box
        className={className}
        maxW="full"
        mx="auto"
        position="relative"
        w="full"
      >
        {/* Title */}
        {title && (
          <m.div
            animate={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
          >
            <Text
              color="fg.default"
              fontSize="2xl"
              fontWeight="bold"
              mb={2}
              textAlign="center"
            >
              {title}
            </Text>
          </m.div>
        )}

        {/* Subtitle */}
        {subtitle && (
          <m.div
            animate={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: -20 }}
            transition={{ delay: 0.1, duration: 0.5 }}
          >
            <Text color="fg.subtle" fontSize="md" mb={6} textAlign="center">
              {subtitle}
            </Text>
          </m.div>
        )}

        <HStack
          align="flex-start"
          flexWrap="wrap"
          gap={8}
          justify="center"
          maxW="full"
          minW={0}
          w="full"
        >
          {/* Chart SVG */}
          <Box
            maxW="full"
            minW={0}
            position="relative"
            w={`min(100%, ${size}px)`}
          >
            <m.svg
              animate={{ opacity: 1, rotate: 0, scale: 1 }}
              height="auto"
              initial={{ opacity: 0, rotate: -180, scale: 0.95 }}
              style={{
                aspectRatio: "1 / 1",
                display: "block",
                overflow: "visible",
                width: "100%",
              }}
              transition={{
                duration: animationDuration,
                ease: "easeOut",
              }}
              viewBox={`${-10} ${-10} ${size + 20} ${size + 20}`}
              width="100%"
            >
              {/* Segments with rounded corners */}
              {segments.map((segment, index) => (
                <m.path
                  animate={{
                    opacity: 1,
                    pathLength: 1,
                  }}
                  d={segment.path}
                  fill="none"
                  initial={{
                    opacity: 0,
                    pathLength: 0,
                  }}
                  key={segment.label}
                  onMouseLeave={handleMouseLeave}
                  onMouseMove={(e) => handleMouseMove(e, index)}
                  stroke={segment.color}
                  strokeLinecap="round"
                  strokeWidth={thickness}
                  style={{
                    cursor: "pointer",
                    opacity:
                      hoveredIndex !== null && hoveredIndex !== index ? 0.5 : 1,
                    transformOrigin: "center",
                    transition: "opacity 0.2s ease",
                  }}
                  transition={{
                    opacity: {
                      delay: index * 0.1,
                      duration: 0.3,
                    },
                    pathLength: {
                      delay: index * 0.1,
                      duration: animationDuration,
                      ease: "easeInOut",
                    },
                  }}
                  whileHover={{
                    filter: "brightness(1.2)",
                    strokeWidth: thickness + 5,
                  }}
                />
              ))}
            </m.svg>

            {/* Center Content */}
            <Box
              left="50%"
              pointerEvents="none"
              position="absolute"
              textAlign="center"
              top="50%"
              transform="translate(-50%, -50%)"
            >
              {centerContent ? (
                <m.div
                  animate={{ opacity: 1, scale: 1 }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                >
                  {centerContent}
                </m.div>
              ) : (
                <m.div
                  animate={{ opacity: 1, scale: 1 }}
                  initial={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: 0.5, duration: 0.5 }}
                >
                  <Text color="fg.default" fontSize="3xl" fontWeight="bold">
                    {total}
                  </Text>
                  <Text color="fg.subtle" fontSize="sm">
                    Total
                  </Text>
                </m.div>
              )}
            </Box>

            {/* Tooltip */}
            {showTooltip && hoveredIndex !== null && (
              <AnimatePresence>
                <m.div
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  style={{
                    left: tooltipPosition.x,
                    pointerEvents: "none",
                    position: "absolute",
                    top: tooltipPosition.y,
                    transform: "translate(-50%, -120%)",
                    zIndex: 10,
                  }}
                  transition={{ duration: 0.2 }}
                >
                  <Box
                    _dark={{
                      bg: "gray.700",
                    }}
                    bg="gray.800"
                    borderRadius="md"
                    boxShadow="lg"
                    color="white"
                    px={3}
                    py={2}
                    whiteSpace="nowrap"
                  >
                    <Text fontSize="sm" fontWeight="semibold">
                      {segments[hoveredIndex]?.label}
                    </Text>
                    <Text color="gray.300" fontSize="xs">
                      {segments[hoveredIndex]?.value} (
                      {segments[hoveredIndex]?.percentage.toFixed(1)}%)
                    </Text>
                    {segments[hoveredIndex]?.description && (
                      <Text color="gray.400" fontSize="xs" mt={1}>
                        {segments[hoveredIndex]?.description}
                      </Text>
                    )}
                  </Box>
                </m.div>
              </AnimatePresence>
            )}
          </Box>

          {/* Legend */}
          {showLegend && (
            <VStack align="stretch" gap={2} maxW="full" minW={0} w="200px">
              {segments.map((segment, index) => (
                <m.div
                  animate={{ opacity: 1, x: 0 }}
                  initial={{ opacity: 0, x: -20 }}
                  key={segment.label}
                  transition={{
                    delay: index * 0.1 + 0.3,
                    duration: 0.5,
                  }}
                >
                  <HStack
                    _hover={{
                      bg: "bg.subtle",
                    }}
                    borderRadius="md"
                    cursor="pointer"
                    onMouseEnter={() => handleLegendHover(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    opacity={
                      hoveredIndex !== null && hoveredIndex !== index ? 0.5 : 1
                    }
                    p={2}
                    transition="opacity 0.2s ease, background-color 0.2s ease"
                  >
                    <Box
                      bg={segment.color}
                      borderRadius="sm"
                      flexShrink={0}
                      h={4}
                      w={4}
                    />
                    <VStack align="stretch" flex={1} gap={0}>
                      <HStack justify="space-between">
                        <Text
                          color="fg.default"
                          fontSize="sm"
                          fontWeight="medium"
                        >
                          {segment.label}
                        </Text>
                        <Text
                          color="fg.default"
                          fontSize="sm"
                          fontWeight="semibold"
                        >
                          {segment.percentage.toFixed(1)}%
                        </Text>
                      </HStack>
                      <Text color="fg.subtle" fontSize="xs">
                        {segment.value.toLocaleString()}
                      </Text>
                    </VStack>
                  </HStack>
                </m.div>
              ))}
            </VStack>
          )}
        </HStack>
      </Box>
    </LazyMotion>
  )
}

export { DonutChart }
export default DonutChart
